import React, { useCallback, useState } from 'react';
import { useNotify, useRefresh, useEditController } from 'react-admin';
import { Edit } from 'ra-ui-materialui';
import { FormSpy } from 'react-final-form';
import { withStyles } from '@material-ui/styles';

import { getInitials, removePathEnd } from '../../core/utils';
import FormTitleBar from '../../core/components/FormTitleBar';
import EditToolbar from '../../core/components/edit/EditToolbar';
import NavigationPrompt from '../../core/components/NavigationPrompt';
import LeaveConfirm from '../../core/components/LeaveConfirmWrapper';
import { getIcError } from '../../core/providers/utils';
import CustomSimpleForm from '../../core/components/form/CustomSimpleForm';
import FundTagApi from '../../core/apis/FundTagApi';
import FundTagsAssociationsApi from '../../core/apis/FundTagsAssociationsApi';

import FundTagSummary from './FundTagSummary';

const styles = () => ({
  noActions: {
    marginTop: '0px'
  }
});

const FundTagTitle = ({ record }) => {
  return (
    <FormTitleBar
      name={record.name}
      showAvatar
      avatarContent={getInitials(record.name)}
    />
  );
};

const FundTagEdit = props => {
  const {
    classes,
    id,
    basePath,
    resource,
    history,
    hasEditPermission,
    hasDeletePermission
  } = props;

  const displayToolbar = props.match.isExact;
  const notify = useNotify();
  const refresh = useRefresh();
  const { record } = useEditController(props);
  const [resetFilter, setResetFilter] = useState(false);
  const [whenBlockNavigation, setWhenBlockNavigation] = useState(false);

  const onBeforeDelete = () => {
    // Unblock navigation before deleting
    setWhenBlockNavigation(false);
  };

  const handleFormIsDirty = useCallback(
    dirty => {
      // Block navigation when the form is dirty
      setWhenBlockNavigation(dirty);
    },
    [setWhenBlockNavigation]
  );

  const onSave = async values => {
    setResetFilter(false);
    let hasError = false;
    let hasModification = false;
    if (
      record.name !== values.name ||
      record.description !== values.description
    ) {
      hasModification = true;
      const payload = { name: values.name, description: values.description };
      await FundTagApi.update(record.id, payload).catch(err => {
        hasError = true;
        notify('ra.notification.server', {
          type: 'warning',
          messageArgs: {
            message: 'Fund Tag name has not been updated. ' + getIcError(err),
            isForkedWarning: true
          }
        });
      });
    }

    const fundIdsToAdd = values.fundIds
      .filter(id => !record.fundIds.includes(id))
      .map(id => {
        return { fundTagId: `${record.id}`, fundId: `${id}` };
      });
    if (fundIdsToAdd.length > 0) {
      hasModification = true;
      await FundTagsAssociationsApi.create(fundIdsToAdd).catch(err => {
        hasError = true;
        notify('ra.notification.server', {
          type: 'warning',
          messageArgs: {
            message:
              'New Fund Tag associations have not been created. ' +
              getIcError(err),
            isForkedWarning: true
          }
        });
      });
    }

    const fundIdsToDelete = record.fundIds
      .filter(id => !values.fundIds.includes(id))
      .map(id => {
        return { fundTagId: `${record.id}`, fundId: `${id}` };
      });
    if (fundIdsToDelete.length > 0) {
      hasModification = true;
      await FundTagsAssociationsApi.delete(fundIdsToDelete).catch(err => {
        hasError = true;
        notify('ra.notification.server', {
          type: 'warning',
          messageArgs: {
            message:
              'Fund Tag associations have not been deleted. ' + getIcError(err),
            isForkedWarning: true
          }
        });
      });
    }

    if (!hasError && hasModification) {
      notify('ra.notification.custom_updated_message');
      setResetFilter(true);
      refresh();
    } else {
      setResetFilter(true);
      refresh();
    }
  };

  return (
    <React.Fragment>
      <NavigationPrompt
        when={whenBlockNavigation}
        adjustMatchingPath={removePathEnd}
        hasEditPermission={hasEditPermission}
      >
        {(isOpen, onConfirm, onCancel) => (
          <LeaveConfirm
            isOpen={isOpen}
            onConfirm={onConfirm}
            onClose={onCancel}
          />
        )}
      </NavigationPrompt>
      <Edit
        id={id}
        basePath={basePath}
        resource={resource}
        undoable={false}
        title={<FundTagTitle />}
        classes={{ noActions: classes.noActions }}
      >
        <CustomSimpleForm
          save={onSave}
          margin="normal"
          variant="standard"
          toolbar={
            displayToolbar ? (
              <EditToolbar
                history={history}
                onBeforeDelete={onBeforeDelete}
                hasEditPermission={hasEditPermission}
                hasDeletePermission={hasDeletePermission}
                deleteContent="ra.message.confirm_delete_tag"
              />
            ) : null
          }
        >
          <FormSpy
            subscription={{ dirty: true }}
            onChange={e => handleFormIsDirty(e.dirty)}
          />
          <FundTagSummary
            resetFilter={resetFilter}
            hasEditPermission={hasEditPermission}
          />
        </CustomSimpleForm>
      </Edit>
    </React.Fragment>
  );
};

export default withStyles(styles)(FundTagEdit);
